import { AnimatePresence, motion } from "framer-motion";
import { Settings, X } from "lucide-react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetStatsQuery } from "../api/nebula-stats/nebulaStatsApi";
import { NebulaIcon } from "../components/icons";
import {
  useChatPlatform,
  useChatPlatformActions,
} from "../context/ChatPlatformContext";
import DecisionTab from "../modules/portfolio-ai/components/DecisionTab";
import HeaderTab from "../modules/portfolio-ai/components/HeaderTab";
import PortfolioAssistantSettings from "../modules/portfolio-ai/components/settings/PortfolioAssistantSettings";
import { setSettingsPanelOpen } from "../modules/portfolio-ai/store/nebulaBrainSlice";
import { useSidebarWidth } from "./hooks/useSidebarWidth";

const DECISION_TABS = [
  { id: "all", label: "Tất cả" },
  {
    id: "planned",
    label: "Kế hoạch",
    className: "!bg-white",
    filter: [
      "REBALANCE_PLANNED",
      "CROSS_ASSET_REALLOCATION_PLANNED",
      "HEDGE_PLANNED",
      "FIXED_INCOME_PLANNED",
    ].join(","),
  },
  {
    id: "executed",
    label: "Thực hiện",
    filter: [
      "REBALANCE_EXECUTED",
      "REBALANCE_PARTIAL",
      "REBALANCE_SUPERSEDED",
      "CROSS_ASSET_REALLOCATION_EXECUTED",
      "HEDGE_EXECUTED",
      "HEDGE_MARGIN_CALL_CLOSE",
      "FIXED_INCOME_EXECUTED",
    ].join(","),
  },
];

const DECISION_TAB_MAP = Object.fromEntries(
  DECISION_TABS.map((tab) => [tab.id, tab]),
);

const MODEL_NAME = {
  orbit: "Orbit",
  horizon: "Horizon",
};

export default function NebulaAssistantSidebar({ defaultTab = "all" }) {
  const [activeTab, setActiveTab] = React.useState(defaultTab);
  const { data: statsData } = useGetStatsQuery({});
  const assistantName =
    MODEL_NAME[statsData?.data?.profile?.strategyModel] ?? "Nebula Assistant";
  const dispatch = useDispatch();
  const showSettings = useSelector(
    (state) => state.nebulaBrain.settingsPanelOpen,
  );
  const { isRightPanelOpen } = useChatPlatform();
  const { setIsRightPanelOpen } = useChatPlatformActions();
  const [startTransition] = React.useTransition();
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  const [localOpen, setLocalOpen] = React.useState(
    isMobile ? isRightPanelOpen : true,
  );

  const { width, startResize } = useSidebarWidth({
    side: "right",
    defaultWidth: 387,
    storageKey: "portfolio_assistant_width",
  });

  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 950);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  React.useEffect(() => {
    dispatch(setSettingsPanelOpen(false));
  }, [dispatch]);

  React.useEffect(() => {
    if (!isMobile) {
      setLocalOpen(true);
      if (!isRightPanelOpen) {
        setIsRightPanelOpen(true);
      }
    } else {
      setLocalOpen(isRightPanelOpen);
    }
  }, [isRightPanelOpen, isMobile, setIsRightPanelOpen]);

  const handleToggle = React.useCallback(
    (open) => {
      if (!isMobile && !open) return;

      setLocalOpen(open);
      startTransition(() => {
        setIsRightPanelOpen(open);
      });
    },
    [setIsRightPanelOpen, isMobile],
  );

  const handleBack = React.useCallback(
    () => dispatch(setSettingsPanelOpen(false)),
    [dispatch],
  );

  const tabs = React.useMemo(
    () => DECISION_TABS.map(({ id, label }) => ({ id, label })),
    [],
  );
  const activeTabConfig = DECISION_TAB_MAP[activeTab];

  const innerContent = React.useMemo(
    () => (
      <div className="flex-1 flex flex-col min-h-0 relative">
        {/* Main Content (Always mounted to prevent lag/leaks) */}
        <div
          className={`flex flex-col h-full transition-opacity duration-300 ${showSettings ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        >
          {/* Header Section */}
          <div className="flex items-center justify-between px-3 h-14 bg-app-bg border-b border-[#ECECED]">
            <div className="flex items-center gap-2 min-w-0">
              <NebulaIcon
                size={32}
                showBackground={true}
                className="flex-shrink-0"
              />
              <div className="flex flex-col justify-center gap-[3px]">
                <span className="text-[13px] font-semibold text-gray-900 leading-[1.1] truncate">
                  {assistantName}
                </span>
                <div className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#34D399] animate-pulse" />
                  <span className="text-[11px] font-medium leading-[1.1] text-gray-500">
                    Đang hoạt động
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                data-tour-id="nebula-settings-button"
                onClick={() => dispatch(setSettingsPanelOpen(true))}
                className="w-8 h-8 rounded-full border border-[#ECECED] bg-white flex items-center justify-center text-[#3C3C43] hover:bg-gray-50 transition-colors"
              >
                <Settings size={14} />
              </button>

              {isMobile && (
                <button
                  onClick={() => handleToggle(false)}
                  className="w-8 h-8 rounded-full border border-[#ECECED] bg-white flex items-center justify-center text-[#3C3C43] hover:bg-gray-50 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          <HeaderTab
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="px-[20px]"
          />

          <div className="flex-1 overflow-hidden relative">
            <motion.div
              key={activeTab}
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute inset-0"
            >
              <DecisionTab
                decision={activeTabConfig?.filter}
                className={activeTabConfig?.className}
              />
            </motion.div>
          </div>
        </div>

        <AnimatePresence>
          {showSettings && (
            <PortfolioAssistantSettings key="settings" onBack={handleBack} />
          )}
        </AnimatePresence>
      </div>
    ),
    [
      activeTab,
      assistantName,
      showSettings,
      isMobile,
      tabs,
      activeTabConfig,
      handleBack,
      handleToggle,
    ],
  );

  return (
    <>
      <motion.div
        data-tour-id="nebula-decision-log"
        className="portfolio-assistant flex flex-col h-full bg-white border-l border-[#ECECED] overflow-hidden"
        style={{
          position: isMobile ? "fixed" : "relative",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: isMobile ? 50 : "auto",
          willChange: isMobile ? "transform" : "width",
          ...(isMobile ? {} : { width: `${width}px` }),
        }}
        initial={false}
        animate={
          isMobile
            ? {
                x: localOpen ? 0 : "100%",
                width: "100%",
                opacity: 1,
                pointerEvents: localOpen ? "auto" : "none",
                borderLeftWidth: localOpen ? 1 : 0,
              }
            : {
                x: 0,
                opacity: 1,
                pointerEvents: "auto",
                borderLeftWidth: 1,
              }
        }
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 220,
          mass: 0.8,
        }}
      >
        {!isMobile && (
          <div
            onMouseDown={startResize}
            className="absolute top-0 bottom-0 left-0 w-1 cursor-ew-resize hover:bg-blue-400/50 z-20 -translate-x-px"
          />
        )}

        {innerContent}
      </motion.div>

      <AnimatePresence>
        {!localOpen && isMobile && (
          <motion.div
            initial={{ scale: 0, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: 20 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-6 right-6 z-[60]"
          >
            <button
              onClick={() => handleToggle(true)}
              className="w-14 h-14 rounded-full bg-white border border-[#ECECED] shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex items-center justify-center hover:bg-gray-50 transition-colors group"
            >
              <NebulaIcon
                size={32}
                showBackground={false}
                className="transition-transform group-hover:rotate-12"
              />
              <div className="absolute top-3 right-3 w-3 h-3 bg-[#34D399] rounded-full border-2 border-white animate-pulse" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
